/*
Copyright (C) 2026  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";

import { ThemePalette } from "@src/components/Theme";
import Button from "@src/components/ui/Button";
import StatusIcon from "@src/components/ui/StatusComponents/StatusIcon";
import TextInput from "@src/components/ui/TextInput";
import prerequisitesSource from "@src/sources/PrerequisitesSource";
import notificationStore from "@src/stores/NotificationStore";
import wizardStore from "@src/stores/WizardStore";
import DomUtils from "@src/utils/DomUtils";

import type {
  PrerequisiteCheck,
  Prerequisites,
  WizardData,
} from "@src/@types/WizardData";

// Destinations whose providers implement the 'target-prerequisites' action.
export const PREREQUISITE_TARGETS = ["azure"];

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: auto;
  padding-right: 8px;
`;
const Intro = styled.div`
  color: ${ThemePalette.grayscale[4]};
  margin-bottom: 16px;
  line-height: 20px;
`;
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 8px 0 16px 0;
`;
const Summary = styled.div<{ ok: boolean }>`
  color: ${props => (props.ok ? ThemePalette.success : ThemePalette.alert)};
`;
const Muted = styled.span`
  color: ${ThemePalette.grayscale[4]};
  font-size: 12px;
`;
const List = styled.div`
  border: 1px solid ${ThemePalette.grayscale[2]};
  border-radius: 4px;
  padding: 4px 16px;
`;
const CheckRow = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid ${ThemePalette.grayscale[1]};
  &:last-child {
    border-bottom: none;
  }
`;
const CheckBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  flex-grow: 1;
`;
const Steps = styled.ol`
  margin: 2px 0 0 0;
  padding-left: 18px;
`;
const Step = styled.li`
  margin-bottom: 6px;
`;
const Command = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  font-family: monospace;
  font-size: 12px;
  background: ${ThemePalette.grayscale[7]};
  padding: 6px 8px;
  border-radius: 4px;
  word-break: break-all;
`;
const CopyLink = styled.span`
  color: ${ThemePalette.primary};
  cursor: pointer;
  font-family: sans-serif;
  flex-shrink: 0;
`;
const CreateBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px 16px;
  padding: 10px 12px;
  background: ${ThemePalette.grayscale[7]};
  border-radius: 4px;
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const Label = styled.div`
  font-size: 10px;
  font-weight: 500;
  color: ${ThemePalette.grayscale[3]};
  text-transform: uppercase;
`;

const STATUS_ICON: { [status: string]: string } = {
  ok: "COMPLETED",
  warning: "WARNING",
  missing: "ERROR",
  error: "ERROR",
};

const VNET_FIELDS = [
  { key: "vnet_name", label: "Virtual network name" },
  { key: "address_space", label: "Address space" },
  { key: "subnet_name", label: "Subnet name" },
  { key: "subnet_prefix", label: "Subnet range" },
];

const envOf = (data: WizardData) => {
  const opts = data.destOptions || {};
  const env: any = {};
  ["location", "resource_group", "vm_size", "storage_account"].forEach(k => {
    if (opts[k] != null && opts[k] !== "") env[k] = opts[k];
  });
  return env;
};

const instanceOf = (data: WizardData) => {
  const vms = data.selectedInstances || [];
  return {
    num_cpu: Math.max(1, ...vms.map(v => v.num_cpu || 1)),
    memory_mb: Math.max(1024, ...vms.map(v => v.memory_mb || 1024)),
  };
};

export const prerequisitesSignature = (data: WizardData) =>
  JSON.stringify([data.target?.id, envOf(data), instanceOf(data)]);

export const prerequisitesReady = (data: WizardData): boolean => {
  if (!PREREQUISITE_TARGETS.includes(data.target?.type || "")) {
    return true;
  }
  const p = data.prerequisites;
  if (!p || !p.result || p.signature !== prerequisitesSignature(data)) {
    return false;
  }
  return p.result.ready;
};

type Props = { wizardData: WizardData };
type State = {
  checking: boolean;
  creating: string | null;
  confirm: string | null;
  options: { [key: string]: string };
};

@observer
class WizardPrerequisites extends React.Component<Props, State> {
  state: State = { checking: false, creating: null, confirm: null, options: {} };

  componentDidMount() {
    const p = this.props.wizardData.prerequisites;
    if (!p || p.signature !== prerequisitesSignature(this.props.wizardData)) {
      this.check();
    }
  }

  get prerequisites(): Prerequisites {
    return this.props.wizardData.prerequisites || { result: null, signature: null, changed: false };
  }

  update(changes: Partial<Prerequisites>) {
    wizardStore.updateData({ prerequisites: { ...this.prerequisites, ...changes } });
  }

  async check() {
    const data = this.props.wizardData;
    if (!data.target) return;
    this.setState({ checking: true });
    try {
      const result = await prerequisitesSource.check({
        endpointId: data.target.id,
        env: envOf(data),
        instance: instanceOf(data),
      });
      this.update({ result, signature: prerequisitesSignature(data) });
    } catch (err: any) {
      notificationStore.alert(`Checking the target failed: ${err?.data?.description || err?.message || err}`, "error");
    } finally {
      this.setState({ checking: false });
    }
  }

  async create(check: PrerequisiteCheck) {
    const data = this.props.wizardData;
    if (!data.target || !check.create) return;
    this.setState({ creating: check.id });
    try {
      const out = await prerequisitesSource.create({
        endpointId: data.target.id,
        env: envOf(data),
        item: check.create.item,
        options: { ...(check.create.defaults || {}), ...this.state.options },
      });
      notificationStore.alert(`Created: ${out.detail}`, "success");
      this.update({ changed: true });
      this.setState({ confirm: null, options: {} });
      await this.check();
    } catch (err: any) {
      notificationStore.alert(`Could not create it: ${err?.data?.description || err?.message || err}`, "error");
    } finally {
      this.setState({ creating: null });
    }
  }

  renderCreate(check: PrerequisiteCheck) {
    if (!check.create || check.status === "ok") return null;
    const busy = this.state.creating === check.id;
    if (this.state.confirm !== check.id) {
      return (
        <div>
          <Button secondary onClick={() => this.setState({ confirm: check.id })} disabled={Boolean(this.state.creating)}>
            {check.create.label}
          </Button>
        </div>
      );
    }
    const defaults = check.create.defaults || {};
    return (
      <CreateBox>
        {check.create.item === "virtual_network"
          ? VNET_FIELDS.map(f => (
              <Field key={f.key}>
                <Label>{f.label}</Label>
                <TextInput
                  width="180px"
                  value={this.state.options[f.key] ?? defaults[f.key] ?? ""}
                  onChange={e => this.setState({ options: { ...this.state.options, [f.key]: e.target.value } })}
                />
              </Field>
            ))
          : <Muted>SwiftIQ will do this with the Azure endpoint's service principal.</Muted>}
        <Button onClick={() => this.create(check)} disabled={busy}>
          {busy ? "Creating…" : "Confirm and create in Azure"}
        </Button>
        <Button secondary onClick={() => this.setState({ confirm: null, options: {} })} disabled={busy}>
          Cancel
        </Button>
      </CreateBox>
    );
  }

  renderStep(step: { text: string; command?: string | null }, index: number) {
    return (
      <Step key={index}>
        {step.text}
        {step.command ? (
          <Command>
            <span style={{ flexGrow: 1 }}>{step.command}</span>
            <CopyLink onClick={() => DomUtils.copyTextToClipboard(step.command!, "Command copied")}>Copy</CopyLink>
          </Command>
        ) : null}
      </Step>
    );
  }

  renderCheck(check: PrerequisiteCheck) {
    return (
      <CheckRow key={check.id}>
        <StatusIcon status={STATUS_ICON[check.status] || "WARNING"} />
        <CheckBody>
          <div>
            {check.label}
            {check.detail ? <Muted> — {check.detail}</Muted> : null}
          </div>
          {check.status !== "ok" && check.steps.length ? (
            <>
              <Muted>An Azure administrator needs to do this:</Muted>
              <Steps>{check.steps.map((s, i) => this.renderStep(s, i))}</Steps>
            </>
          ) : null}
          {this.renderCreate(check)}
        </CheckBody>
      </CheckRow>
    );
  }

  render() {
    const p = this.prerequisites;
    const stale = p.result && p.signature !== prerequisitesSignature(this.props.wizardData);
    const result = stale ? null : p.result;
    return (
      <Wrapper>
        <Intro>
          SwiftIQ checks that the target is ready for this migration, using the options you chose. Existing
          resources are used; for anything missing it can create it after you confirm, if its credentials allow.
          Otherwise it shows what an administrator needs to do.
        </Intro>
        <Actions>
          <Button onClick={() => this.check()} disabled={this.state.checking || Boolean(this.state.creating)}>
            {this.state.checking ? "Checking…" : "Check again"}
          </Button>
          {result ? (
            <Summary ok={result.ready}>
              {result.ready
                ? "The target is ready."
                : "Resolve the items marked in red, then check again."}
            </Summary>
          ) : null}
          {stale ? <Muted>Target options changed since the last check.</Muted> : null}
        </Actions>
        {result && result.supported === false ? (
          <Muted>This target has no prerequisite checks.</Muted>
        ) : null}
        {result && result.checks.length ? <List>{result.checks.map(c => this.renderCheck(c))}</List> : null}
      </Wrapper>
    );
  }
}

export default WizardPrerequisites;
